import * as React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridOverlay,
} from '@mui/x-data-grid';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  FiscalYearUsageStatistics
} from '../../models';

import {
  API_URL,
  EMPTY_STRING
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

interface UsageStatisticsFiscalYearProps {
  fiscalYear: string;
}

const UsageStatisticsFiscalYear: React.FC<UsageStatisticsFiscalYearProps> = (props) => {

  const { getAccessToken } = useAuth0User();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [statistics, setStatistics] = React.useState<FiscalYearUsageStatistics>(new FiscalYearUsageStatistics());

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      margin: theme.spacing(2, 2, 2, 2),
    },
    grid: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    gridContainer: {
      flexGrow: 1,
      margin: theme.spacing(1, 0, 0, 0),
    },
    noRows: {
      padding: theme.spacing(2, 0, 2, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
  * Calculate total percentages
  */
  const calculatePercentage = (n: number, d: number) => {
    if (d > 0) {
      return ((n / d) * 100).toFixed(0);
    } else {
      return '0';
    }
  }

  /*
* Grid column definitions
*/
  const columns = (): Array<GridColDef> => {

    return [
      {
        field: 'category',
        headerName: 'Category',
        width: 200,
        type: 'string',
        align: 'left'
      },
      {
        field: 'lodgeNights',
        headerName: 'Nights',
        headerAlign: 'center',
        width: 90,
        type: 'string',
        align: 'right'
      }
    ];

  }

  /*
  * Fetch fiscal year statistics
  */
  const fetchStatistics = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    params.append('year', props.fiscalYear);

    const url = new URL(`${API_URL}/v1/requests/usagestatistics/fiscalyear`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Usage statistics for fiscal year not retrieved');
        }
        return response.json()
      })
      .then((s: FiscalYearUsageStatistics) => {
        setIsLoading(false);
        setStatistics(s);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Usage statistics for fiscal year not retrieved');
      });
  }

  /*
* Overlay displayed when no data
*/
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No fiscal year statistics to display</div>
      </GridOverlay>
    );
  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if (props.fiscalYear !== EMPTY_STRING) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fiscalYear]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container size={12} justifyContent="center" >
        <Typography align="center">TOTAL NIGHTS {statistics.grandTotal}</Typography>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography align="center">Ski Season {statistics.skiSeasonTotal} ({calculatePercentage(statistics.skiSeasonTotal, statistics.grandTotal)}%)</Typography>
        </Grid>
        <Grid size={6}>
          <Typography align="center">Off Season {statistics.offSeasonTotal} ({calculatePercentage(statistics.offSeasonTotal, statistics.grandTotal)}%)</Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={2} sx={styles.gridContainer}>
        <Grid size={3}>
          <Grid container>
            <Typography>Ski Season Members {statistics.skiSeasonMemberTotal} ({calculatePercentage(statistics.skiSeasonMemberTotal, statistics.skiSeasonTotal)}%)</Typography>
          </Grid>
          <Grid container>
            <DataGrid
              columns={columns()}
              slots={{
                noRowsOverlay: noRowsOverlay
              }}
              density="compact"
              disableColumnSelector
              disableRowSelectionOnClick
              getRowId={(row) => row.rowID}
              hideFooter
              loading={isLoading}
              rows={statistics.skiSeasonMembers}
              sx={{
                ...styles.grid,
                height: 'auto', // Explicitly tell the Grid its height is auto
                '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
              }}
            />
          </Grid>
        </Grid>
        <Grid size={3}>
          <Grid container>
            <Typography>Ski Season Guests {statistics.skiSeasonGuestTotal} ({calculatePercentage(statistics.skiSeasonGuestTotal, statistics.skiSeasonTotal)}%)</Typography>
          </Grid>
          <Grid container>
            <DataGrid
              columns={columns()}
              slots={{
                noRowsOverlay: noRowsOverlay
              }}
              density="compact"
              disableColumnSelector
              disableRowSelectionOnClick
              getRowId={(row) => row.rowID}
              hideFooter
              loading={isLoading}
              rows={statistics.skiSeasonGuests}
              sx={{
                ...styles.grid,
                height: 'auto', // Explicitly tell the Grid its height is auto
                '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
              }}
            />
          </Grid>
        </Grid>
        <Grid size={3}>
          <Grid container>
            <Typography>Off Season Members {statistics.offSeasonMemberTotal} ({calculatePercentage(statistics.offSeasonMemberTotal, statistics.offSeasonTotal)}%)</Typography>
          </Grid>
          <Grid container>
            <DataGrid
              columns={columns()}
              slots={{
                noRowsOverlay: noRowsOverlay
              }}
              density="compact"
              disableColumnSelector
              disableRowSelectionOnClick
              getRowId={(row) => row.rowID}
              hideFooter
              loading={isLoading}
              rows={statistics.offSeasonMembers}
              sx={{
                ...styles.grid,
                height: 'auto', // Explicitly tell the Grid its height is auto
                '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
              }}
            />
          </Grid>
        </Grid>
        <Grid size={3}>
          <Grid container>
            <Typography>Off Season Guests {statistics.offSeasonGuestTotal} ({calculatePercentage(statistics.offSeasonGuestTotal, statistics.offSeasonTotal)}%)</Typography>
          </Grid>
          <Grid container>
            <DataGrid
              columns={columns()}
              slots={{
                noRowsOverlay: noRowsOverlay
              }}
              density="compact"
              disableColumnSelector
              disableRowSelectionOnClick
              getRowId={(row) => row.rowID}
              hideFooter
              loading={isLoading}
              rows={statistics.offSeasonGuests}
              sx={{
                ...styles.grid,
                height: 'auto', // Explicitly tell the Grid its height is auto
                '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  // #endregion

}

export default UsageStatisticsFiscalYear;